'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { format, parse, isValid } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar } from './calendar';
import { Input } from './input';
import { cn } from '@/lib/utils';

// Lazy-load chrono-node only when needed (saves ~330KB in widget builds)
type ChronoParser = { parseDate: (text: string) => Date | null };
let chronoCache: Record<string, ChronoParser> | null = null;

const getChronoParser = async (language: string): Promise<ChronoParser | null> => {
  if (!chronoCache) {
    try {
      // Dynamically import chrono-node (loaded as separate chunk in widget builds)
      const chrono = await import('chrono-node');
      chronoCache = {
        en: chrono.en,
        de: chrono.de,
        es: chrono.es,
        fr: chrono.fr,
        ja: chrono.ja,
        pt: chrono.pt,
        zh: chrono.zh,
      };
    } catch {
      return null;
    }
  }

  return chronoCache[language] || chronoCache['en'] || null;
};

type DatePickerProps = {
  date: Date | null;
  setDate: (date: Date | null) => void;
  disabled?: boolean;
  disabledDates?: Set<number> | null;
  disabledDays?: (date: Date) => boolean;
  fromDate?: Date | null;
  toDate?: Date | null;
  align?: 'center' | 'start' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  language?: string;
  placeholder?: string;
  nlpPlaceholder: string;
}

export const DatePicker: FC<DatePickerProps> = ({
  date,
  setDate,
  disabled = false,
  disabledDates = null,
  disabledDays = null,
  fromDate = null,
  toDate = null,
  align = 'center',
  side = 'bottom',
  language = 'en',
  placeholder = "YYYY-MM-DD, MM/DD/YYYY, or MM-DD-YYYY",
  nlpPlaceholder,
}) => {
  const [open, setOpen] = useState(false);

  // State for the input field
  const [inputValue, setInputValue] = useState('');

  // Cached chrono parser (loaded lazily)
  const chronoParserRef = useRef<ChronoParser | null>(null);
  const [chronoLoaded, setChronoLoaded] = useState(false);

  // Check if natural language parsing is supported for the given language
  const isNaturalLanguageSupported = useMemo(() => {
    const supportedLanguages = ['en', 'fr', 'ja'];
    return supportedLanguages.includes(language);
  }, [language]);

  // Text input with NLP is shown for supported languages
  const showTextInput = isNaturalLanguageSupported;

  // Load chrono parser lazily when language changes
  useEffect(() => {
    if (!isNaturalLanguageSupported) return;

    getChronoParser(language).then((parser) => {
      chronoParserRef.current = parser;
      setChronoLoaded(true);
    });
  }, [language, isNaturalLanguageSupported]);

  // Update input when date changes
  useEffect(() => {
    if (date) {
      setInputValue(format(date, 'MM-dd-yyyy'));
    } else {
      setInputValue('');
    }
  }, [date]);

  // Handle direct input changes
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  };

  // Process input on blur
  const handleInputBlur = () => {
    if (!inputValue) {
      setDate(null);
      return;
    }

    let parsedDate;

    // Try natural language parsing first if supported
    if (isNaturalLanguageSupported && chronoParserRef.current) {
      const chronoResults = chronoParserRef.current.parseDate(inputValue);
      if (chronoResults) {
        parsedDate = chronoResults;
      }
    }

    // Fallback to traditional date format parsing if natural language didn't work
    if (!parsedDate || !isValid(parsedDate)) {
      const formats = [
        { format: 'yyyy-MM-dd', sample: '2025-05-28' },
        { format: 'MM/dd/yyyy', sample: '05/28/2025' },
        { format: 'MM-dd-yyyy', sample: '05-28-2025' },
      ];

      for (const formatObj of formats) {
        parsedDate = parse(inputValue, formatObj.format, new Date());
        if (isValid(parsedDate)) break;
      }
    }

    if (isValid(parsedDate)) {
      setDate(parsedDate);
    } else {
      // If invalid, reset to current date
      setInputValue(date ? format(date, 'MM-dd-yyyy') : '');
    }
  };

  // Handle enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  // Convert disabled props to the format expected by Calendar
  const getDisabledMatcher = () => {
    if (disabledDays) {
      return disabledDays;
    }
    if (disabledDates) {
      return (date: Date) => disabledDates.has(date.getTime());
    }
    return undefined;
  };

  // Handle date selection from calendar
  const handleDateSelect = (selectedDate: Date | undefined) => {
    // Only set the date if a valid date is selected, don't allow deselection
    if (selectedDate) setDate(selectedDate);
    setOpen(false); // Close popover when date is selected
  };

  // Handle date selection from embedded calendar (non-English)
  const handleEmbeddedDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) setDate(selectedDate);
  };

  // For non-English speakers, show embedded calendar directly
  if (!showTextInput) {
    return (
      <Calendar
        mode="single"
        selected={date ?? undefined}
        onSelect={handleEmbeddedDateSelect}
        disabled={getDisabledMatcher()}
        fromDate={fromDate ?? undefined}
        toDate={toDate ?? undefined}
        className="w-fit max-w-full mx-auto rounded-md border"
      />
    );
  }

  // For English speakers, show text input with popover calendar
  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <div className="flex">
        <Input
          placeholder={nlpPlaceholder}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={cn('rounded-r-none border-r-0 focus-visible:z-10')}
        />
        <PopoverTrigger
          className="inline-flex items-center justify-center rounded-l-none h-9 w-9 hover:bg-wb-orange/20 border border-input bg-background shadow-xs disabled:opacity-50"
          aria-label="Open calendar"
          disabled={disabled}
        >
          <CalendarIcon className="h-4 w-4" />
        </PopoverTrigger>
      </div>
      <PopoverContent
        className="w-auto p-0 shadow-lg bg-background"
        align={align}
        side={side}
      >
        <Calendar
          mode="single"
          selected={date ?? undefined}
          onSelect={handleDateSelect}
          initialFocus
          disabled={getDisabledMatcher()}
          fromDate={fromDate ?? undefined}
          toDate={toDate ?? undefined}
        />
      </PopoverContent>
    </Popover>
  );
}
